import React from 'react';
import { Card } from '../atoms/Card';
import { formatCurrency } from '../../utils/date';
import { useTheme } from '../../utils/hooks/useTheme';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface ChartDataPoint {
  month: string;
  value: number;
}

interface PortfolioChartProps {
  data: ChartDataPoint[];
  isLoading?: boolean;
}

export const PortfolioChart: React.FC<PortfolioChartProps> = ({
  data,
  isLoading = false
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const gridColor = isDark ? '#374151' : '#e5e7eb';
  const tickColor = isDark ? '#9ca3af' : '#6b7280';

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Valeur du Portfolio (12 derniers mois)
        </h2>
        <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
          Chargement du graphique...
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Valeur du Portfolio (12 derniers mois)
      </h2>
      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis
              dataKey="month"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={60}
              tick={{ fill: tickColor }}
            />
            <YAxis
              fontSize={12}
              tickFormatter={(value) => `${(value / 1000).toFixed(0)}k €`}
              tick={{ fill: tickColor }}
            />
            <Tooltip
              formatter={(value: number) => [formatCurrency(value), 'Valeur']}
              labelFormatter={(label) => `Mois: ${label}`}
              contentStyle={isDark ? { backgroundColor: '#1f2937', borderColor: '#374151', color: '#f3f4f6' } : undefined}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#3B82F6"
              fill="#3B82F6"
              fillOpacity={0.6}
              name="Valeur Portfolio"
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
          Pas assez de données pour afficher le graphique
        </div>
      )}
    </Card>
  );
};
